<template>
  <div>
    <div class="pm-toolbar">
      <div class="pm-seg">
        <div :class="{'on': period === 'next'}" @click="setPeriod('next')" qid="pm-year-next">{{ $t('projects.management.period_next') }}</div>
        <div :class="{'on': period === 'calendar'}" @click="setPeriod('calendar')" qid="pm-year-calendar">{{ $t('projects.management.period_calendar', {year: calendarYear}) }}</div>
      </div>
      <div class="pm-seg" v-if="families.length > 1">
        <div :class="{'on': !family}" @click="family = ''">{{ $t('projects.management.all') }}</div>
        <div v-for="item in families"
             :key="item.key"
             :class="{'on': family === item.key}"
             @click="family = item.key">
          {{ $t('projects.management.families.' + item.key) }}
        </div>
      </div>
      <span class="grow"></span>
      <div class="pm-seg">
        <div :class="{'on': layout === 'wheel'}" @click="layout = 'wheel'" qid="pm-year-wheel">{{ $t('projects.management.wheel') }}</div>
        <div :class="{'on': layout === 'months'}" @click="layout = 'months'" qid="pm-year-months">{{ $t('projects.management.months') }}</div>
      </div>
    </div>

    <div class="pm-year" v-if="layout === 'wheel'">
      <div class="pm-card pm-wheel-card">
        <svg :viewBox="'0 0 ' + size + ' ' + size" class="pm-wheel" qid="pm-wheel">
          <!-- Months; the selected one is dark -->
          <g v-for="(month, index) in months"
             :key="'m' + index"
             class="pm-wheel__month"
             @click="selected = index"
             :qid="'pm-wheel-month-' + index">
            <path :d="ringPath(206, 250, index * 30 + 0.7, (index + 1) * 30 - 0.7)" :class="{'on': selected === index}"/>
            <text :x="point(228, index * 30 + 15)[0]"
                  :y="point(228, index * 30 + 15)[1] + 5"
                  text-anchor="middle"
                  :class="{'on': selected === index}">{{ month.format('MMM') }}</text>
          </g>
          <path :d="ringPath(66, 204, selected * 30 + 0.7, (selected + 1) * 30 - 0.7)" class="pm-wheel__focus"/>
          <circle v-for="radius in [178, 152, 126]"
                  :key="'r' + radius"
                  :cx="center"
                  :cy="center"
                  :r="radius"
                  class="pm-wheel__guide"/>
          <!-- Running projects from start to due date -->
          <path v-for="(arc, index) in arcs"
                :key="'a' + index"
                :d="arc.d"
                class="pm-wheel__arc"
                :class="'pm-s-' + arc.color">
            <title>{{ arc.title }}</title>
          </path>
          <!-- Starts of projects, and planned rounds (dashed) -->
          <circle v-for="(dot, index) in dots"
                  :key="'d' + index"
                  :cx="point(192, dot.angle)[0]"
                  :cy="point(192, dot.angle)[1]"
                  r="6.5"
                  class="pm-wheel__dot"
                  :class="['pm-s-' + dot.color, {'pm-wheel__dot--plan': dot.planned}]"
                  @click="selected = dot.month"
          ><title>{{ dot.title }}</title></circle>
          <line v-if="todayAngle !== null"
                :x1="point(66, todayAngle)[0]"
                :y1="point(66, todayAngle)[1]"
                :x2="point(252, todayAngle)[0]"
                :y2="point(252, todayAngle)[1]"
                class="pm-wheel__today"/>
          <circle :cx="center"
                  :cy="center"
                  r="64"
                  class="pm-wheel__centre"/>
          <text :x="center"
                :y="center - 4"
                text-anchor="middle"
                class="pm-wheel__count">{{ monthItems.total }}</text>
          <text :x="center"
                :y="center + 18"
                text-anchor="middle"
                class="pm-wheel__label">{{ months[selected].format('MMMM') }}</text>
          <text :x="center"
                :y="center + 36"
                text-anchor="middle"
                class="pm-wheel__sub">{{ $tc('projects.management.planned_count', monthItems.planned.length, {count: monthItems.planned.length}) }}</text>
        </svg>
        <div class="pm-legend">
          <span v-for="item in legend" :key="item.key"><i :class="'pm-bg-' + item.color"></i>{{ $t('projects.management.families_short.' + item.key) }}</span>
          <span><i class="pm-legend__plan"></i>{{ $t('projects.management.planned') }}</span>
        </div>
      </div>

      <div class="pm-card">
        <div class="pm-card__head">
          <div>
            <h3 class="pm-month-title">{{ months[selected].format('MMMM YYYY') }}</h3>
            <div class="pm-card__sub">{{ monthSummary }}</div>
          </div>
          <span class="grow"></span>
          <div class="pm-seg pm-seg--sm">
            <div @click="selected = Math.max(0, selected - 1)" qid="pm-month-prev"><font-awesome-icon icon="chevron-left"/></div>
            <div @click="selected = Math.min(11, selected + 1)" qid="pm-month-next"><font-awesome-icon icon="chevron-right"/></div>
          </div>
        </div>
        <div class="pm-empty" v-if="loading"><font-awesome-icon icon="spinner" class="fa-spin mr-2"/>{{ $t('projects.management.loading') }}</div>
        <div class="pm-empty" v-if="!loading && !monthItems.total">{{ $t('projects.management.month_empty') }}</div>

        <template v-for="group in monthGroups">
          <div class="pm-group-title" :key="group.key + '-title'" v-if="group.items.length">{{ $t('projects.management.month_groups.' + group.key) }}</div>
          <div class="pm-month-list" :key="group.key" v-if="group.items.length">
            <div class="pm-mi"
                 v-for="(item, index) in group.items"
                 :key="index"
                 :class="{'pm-mi--plan': group.key === 'planned'}">
              <span class="pm-icon" :class="'pm-c-' + meta(item.type).color"><font-awesome-icon :icon="meta(item.type).icon"/></span>
              <div class="grow">
                <router-link :to="{name: 'projects.view', params: {slug: item.slug}}" class="pm-mi__title">{{ item.title }}</router-link>
                <div class="pm-mi__text">{{ itemText(group.key, item) }}</div>
              </div>
              <template v-if="group.key === 'planned'">
                <span class="pm-link mr-2" v-if="item.due" @click="$emit('run', item)">{{ $t('projects.launcher.run') }}</span>
                <span class="pm-tag" :class="item.due ? 'pm-tag--red' : (item.auto_create ? 'pm-tag--teal' : 'pm-tag--grey')">
                  {{ item.due ? $t('projects.management.due') : (item.auto_create ? $t('projects.management.auto') : $t('projects.management.manual')) }}
                </span>
              </template>
              <ProgressBar v-else-if="group.key === 'running'" :value="item.progress"/>
              <span class="pm-tag pm-tag--grey" v-else>{{ $t('projects.management.starts') }}</span>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Months: a light Gantt with the same data -->
    <div class="pm-card pm-months" v-if="layout === 'months'">
      <div class="pm-months__row pm-months__row--head">
        <div class="pm-months__name"></div>
        <div class="pm-months__cell"
             v-for="(month, index) in months"
             :key="'h' + index"
             :class="{'on': selected === index}"
             @click="selected = index">
          {{ month.format('MMM') }}<i>{{ month.format('YY') }}</i>
        </div>
      </div>
      <div class="pm-empty" v-if="!loading && !rows.length">{{ $t('projects.management.month_empty') }}</div>
      <div class="pm-months__row" v-for="(row, index) in rows" :key="'row' + index">
        <div class="pm-months__name">
          <span class="pm-dot" :class="'pm-bg-' + meta(row.type).color"></span>
          <router-link :to="{name: 'projects.view', params: {slug: row.slug}}">{{ row.title }}</router-link>
        </div>
        <div class="pm-months__track">
          <span class="pm-months__bar"
                v-for="(bar, barIndex) in row.bars"
                :key="barIndex"
                :class="['pm-bg-' + meta(row.type).color, {'pm-months__bar--plan': row.planned}]"
                :style="{left: bar.left + '%', width: bar.width + '%'}"
                v-b-tooltip.hover
                :title="bar.tooltip"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import { typeMeta, allowedFamilies } from '@/views/Projects/Management/projectTypes.js'
  import ProgressBar from '@/views/Projects/Management/ProgressBar.vue';

  // Year plan of the Project management page: an annual wheel with the planned rounds of recurring projects (US 9396)
  export default {
    name: 'YearPlan',
    components: {
      ProgressBar
    },
    data: function() {
      return {
        size: 520,
        center: 260,
        period: 'next',
        layout: 'wheel',
        family: '',
        selected: 0,
        loading: true,
        plan: {projects: [], planned: []}
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      calendarYear() {
        var moment = require('moment');
        return moment().year();
      },
      start() {
        var moment = require('moment');
        return this.period === 'calendar' ? moment().startOf('year') : moment().startOf('month');
      },
      months() {
        let months = [];
        for (let i = 0; i < 12; i++) {
          months.push(this.start.clone().add(i, 'months'));
        }
        return months;
      },
      end() {
        return this.start.clone().add(12, 'months').subtract(1, 'day').endOf('day');
      },
      families() {
        return allowedFamilies(this.getClient);
      },
      legend() {
        return [
          {key: 'audits', color: 'teal'},
          {key: 'risk', color: 'rose'},
          {key: 'bia', color: 'violet'},
          {key: 'records', color: 'blue'},
          {key: 'controls', color: 'amber'}
        ];
      },
      projects() {
        let self = this;
        return _.filter(this.plan.projects, function(project) {
          return !self.family || typeMeta(project.type).family === self.family;
        });
      },
      planned() {
        let self = this;
        return _.filter(this.plan.planned, function(run) {
          return !self.family || typeMeta(run.type).family === self.family;
        });
      },
      todayAngle() {
        var moment = require('moment');
        return this.angleOf(moment());
      },
      dots() {
        let self = this;
        var moment = require('moment');
        let dots = [];
        _.forEach(this.projects, function(project) {
          let angle = self.angleOf(moment(project.start_date));
          if (angle !== null) {
            dots.push({angle: angle, month: Math.floor(angle / 30), color: typeMeta(project.type).color, title: project.title, planned: false});
          }
        });
        _.forEach(this.planned, function(run) {
          let angle = self.angleOf(moment(run.date));
          if (angle !== null) {
            dots.push({angle: angle, month: Math.floor(angle / 30), color: typeMeta(run.type).color, title: run.title, planned: true});
          }
        });
        return dots;
      },
      // Each running project gets an arc on the first inner ring that is free for its period
      arcs() {
        let self = this;
        var moment = require('moment');
        let rings = [178, 152, 126];
        let used = [[], [], []];
        let arcs = [];
        let sorted = _.sortBy(this.projects, 'start_date');
        _.forEach(sorted, function(project) {
          if (!project.end_date) {
            return;
          }
          let from = Math.max(0, self.rawAngle(moment(project.start_date)));
          let to = Math.min(359.5, self.rawAngle(moment(project.end_date)));
          if (to <= 0 || from >= 360 || to - from < 2) {
            return;
          }
          let ring = _.findIndex(used, function(intervals) {
            return !_.some(intervals, function(interval) { return from < interval[1] + 4 && to > interval[0] - 4; });
          });
          if (ring === -1) {
            return;
          }
          used[ring].push([from, to]);
          arcs.push({d: self.arcPath(rings[ring], from, to), color: typeMeta(project.type).color, title: project.title});
        });
        return arcs;
      },
      monthItems() {
        var moment = require('moment');
        let monthStart = this.months[this.selected].clone().startOf('month');
        let monthEnd = monthStart.clone().endOf('month');
        let planned = _.filter(this.planned, function(run) {
          return moment(run.date).isBetween(monthStart, monthEnd, null, '[]');
        });
        let starting = _.filter(this.projects, function(project) {
          return moment(project.start_date).isBetween(monthStart, monthEnd, null, '[]');
        });
        let running = _.filter(this.projects, function(project) {
          return moment(project.start_date).isBefore(monthStart) && (!project.end_date || !moment(project.end_date).isBefore(monthStart));
        });
        return {planned: planned, starting: starting, running: running, total: planned.length + starting.length + running.length};
      },
      monthGroups() {
        return [
          {key: 'planned', items: this.monthItems.planned},
          {key: 'starting', items: this.monthItems.starting},
          {key: 'running', items: this.monthItems.running}
        ];
      },
      monthSummary() {
        return [
          this.$tc('projects.management.summary.planned', this.monthItems.planned.length, {count: this.monthItems.planned.length}),
          this.$tc('projects.management.summary.starting', this.monthItems.starting.length, {count: this.monthItems.starting.length}),
          this.$tc('projects.management.summary.running', this.monthItems.running.length, {count: this.monthItems.running.length})
        ].join(' · ');
      },
      rows() {
        let self = this;
        var moment = require('moment');
        let total = this.end.diff(this.start, 'days') + 1;
        let position = function(date) {
          return Math.max(0, Math.min(100, moment(date).diff(self.start, 'days') / total * 100));
        };
        let rows = [];
        _.forEach(this.projects, function(project) {
          let left = position(project.start_date);
          let right = project.end_date ? position(project.end_date) : left + 1;
          rows.push({slug: project.slug, title: project.title, type: project.type, left: left, bars: [{left: left, width: Math.max(0.8, right - left),
            tooltip: moment(project.start_date).format('D MMM YYYY') + (project.end_date ? ' → ' + moment(project.end_date).format('D MMM YYYY') : '')}]});
        });
        // All planned rounds of a recurring project share one row
        _.forEach(_.groupBy(this.planned, 'slug'), function(runs) {
          rows.push({slug: runs[0].slug, title: runs[0].title, type: runs[0].type, planned: true, left: position(runs[0].date),
            bars: _.map(runs, function(run) {
              return {left: position(run.date), width: 1.2, tooltip: moment(run.date).format('D MMM YYYY')};
            })});
        });
        return _.sortBy(rows, 'left');
      }
    },
    methods: {
      meta(type) {
        return typeMeta(type);
      },
      setPeriod(period) {
        var moment = require('moment');
        this.period = period;
        this.selected = period === 'calendar' ? moment().month() : 0;
        this.loadContent();
      },
      // Degrees from the start of the period, 30 per month; may be outside 0-360
      rawAngle(date) {
        let monthIndex = date.diff(this.start.clone().startOf('month'), 'months');
        let monthStart = this.start.clone().add(monthIndex, 'months');
        let fraction = (date.diff(monthStart, 'days', true)) / monthStart.daysInMonth();
        return (monthIndex + fraction) * 30;
      },
      angleOf(date) {
        let angle = this.rawAngle(date);
        return angle >= 0 && angle < 360 ? angle : null;
      },
      point(radius, degrees) {
        let radians = degrees * Math.PI / 180;
        return [this.center + radius * Math.sin(radians), this.center - radius * Math.cos(radians)];
      },
      ringPath(inner, outer, from, to) {
        let p1 = this.point(outer, from), p2 = this.point(outer, to), p3 = this.point(inner, to), p4 = this.point(inner, from);
        let large = to - from > 180 ? 1 : 0;
        return 'M' + p1.join(' ') + ' A' + outer + ' ' + outer + ' 0 ' + large + ' 1 ' + p2.join(' ') +
          ' L' + p3.join(' ') + ' A' + inner + ' ' + inner + ' 0 ' + large + ' 0 ' + p4.join(' ') + 'Z';
      },
      arcPath(radius, from, to) {
        let p1 = this.point(radius, from), p2 = this.point(radius, to);
        return 'M' + p1.join(' ') + ' A' + radius + ' ' + radius + ' 0 ' + (to - from > 180 ? 1 : 0) + ' 1 ' + p2.join(' ');
      },
      itemText(group, item) {
        var moment = require('moment');
        if (group === 'planned') {
          let every = item.repeat_after + ' ' + this.$t('system.interval_' + (item.repeat_after_interval || '').toLowerCase()).toLowerCase();
          return moment(item.date).format('D MMM') + ' · ' + this.$t('projects.launcher.every').toLowerCase() + ' ' + every;
        }
        let text = moment(item.start_date).format('D MMM');
        if (item.end_date) {
          text += ' → ' + moment(item.end_date).format('D MMM YYYY');
        }
        if (item.parent) {
          text += ' · ' + this.$t('projects.management.round_of') + ' ' + item.parent.title;
        }
        return text;
      },
      loadContent() {
        this.loading = true;
        piincHttp.get('projects/year-plan', { params: {
          client: this.getClient.slug,
          from: this.start.format('YYYY-MM-DD'),
          to: this.end.format('YYYY-MM-DD')
        }}).then((response) => {
          this.plan = response.data;
          this.loading = false;
        }, () => {
          this.loading = false;
        });
      }
    },
    created() {
      if (this.getClient && this.getClient.slug) {
        this.loadContent();
      }
    }
  }
</script>
